<template>
	<div class="overflow-visible">
		<div class="stan-notification" :class="notifyClass">
			<div class="stan-icon" :class="notifyClass">
				<InlineSvg :src="`/images/icons/${notifyIcon}`" />
			</div>
			<div class="notify-content">
				<span class="para-2 text-bold notify-content-title">
					{{ data.title }}
				</span>
				<span class="para-2 notify-content-text">
					{{ data.text }}
				</span>
				<span v-if="notifyType === 'fact'" class="para-2 notify-content-link" @click="interactWithFact()">
					View Analytics ➡️
				</span>
			</div>
			<div>
				<button v-if="notifyType !== 'fact'" class="notify-close" :class="notifyClass" @click="$emit('close')">
					OK
				</button>
				<button v-else class="notify-close" :class="notifyClass" @click="closeFact()">
					<InlineSvg :src="`/images/icons/ic-close.svg`" />
				</button>
			</div>
		</div>
	</div>
</template>
<script>
	/*	Sample use:
		this.$notify({
			type: 'error', // can be 'info', 'warning', 'success', 'error'; can also be 'fact' for a different purpose
			title: 'Something went wrong',
			text: 'Malesuada fermentum tortoruada tum tortor',
			duration: 60000,
		})
	*/
	import { mapGetters } from 'vuex'

	export default {
		name: 'StanNotification',
		props: {
			/**
			 * @typedef {Object} NotificationObject
			 * @property {'error'|'info'|'warning'|'success'} type
			 * @property {String} title
			 * @property {String} text
			 * @property {Number} duration
			 */
			/**
			 * @param {NotificationObject} data
			 */
			data: {
				type: Object,
				default() {
					return {}
				},
			},
		},
		data() {
			return {
				notificationsStyle: {
					info: { class: 'notify-info', icon: 'info' },
					warning: { class: 'notify-warning', icon: 'ic-warning-circle.svg' },
					success: { class: 'notify-success', icon: 'ic-check-circle.svg' },
					error: { class: 'notify-error', icon: 'ic-warning-circle.svg' },
					fact: { class: 'notify-fact', icon: 'fact' },
				},
			}
		},
		computed: {
			...mapGetters('Auth', ['user']),
			notifyType() {
				const nt = 'data' in this.data && 'type' in this.data.data ? this.data.data.type.toLowerCase() : 'info'
				return nt in this.notificationsStyle ? nt : 'info'
			},
			notifyClass() {
				return this.notificationsStyle[this.notifyType].class
			},
			notifyIcon() {
				return this.notificationsStyle[this.notifyType].icon
			},
		},
		methods: {
			interactWithFact() {
				this.$router.push({ name: 'analytics' })
				this.$stanAnalytics('creator-fact-interacted', {
					meta: { user_id: this.user.user_id, username: this.user.username },
				})
				this.$emit('close')
			},
			closeFact() {
				this.$stanAnalytics('creator-fact-dismissed', {
					meta: { user_id: this.user.user_id, username: this.user.username },
				})
				this.$emit('close')
			},
		},
	}
</script>
<style lang="scss" scoped>
	.stan-notification {
		border-radius: 15px;
		box-shadow: 0 6px 8px rgb(71 25 117 / 9%);
		display: grid;
		gap: 10px;
		align-items: center;
		width: fit-content;
		height: 100%;
		margin: auto;
		padding: 1rem 2rem;
		margin-bottom: 1rem;
		background: var(--stan-primary-dark-color);
		grid-template-columns: 36px auto 36px;
		color: white;

		.notify-content {
			margin: auto;
			margin-right: 4px;
		}

		.notify-info {
			color: var(--stan-positive-soft-color);
		}
		.notify-warning {
			color: var(--stan-negative-primary-color);
		}
		.notify-success {
			color: var(--stan-positive-soft-color);
		}
		.notify-error {
			color: var(--stan-negative-primary-color);
		}
		&.notify-fact {
			background: var(--stan-white);
			box-shadow: 0px 20px 100px rgba(0, 16, 109, 0.1);
			color: var(--stan-text-dark-color);
			border: 2px solid var(--stan-gray-strike-color);
			padding: 25px;
			overflow: visible;
			grid-template-columns: 0px auto 36px;
			.notify-content {
				.notify-content-text {
					font-weight: 400 !important;
					font-size: 1rem;
					line-height: 1.375rem;
				}
				.notify-content-link {
					font-weight: 500 !important;
					font-size: 1rem;
					line-height: 1.375rem;
					cursor: pointer;
					color: var(--stan-primary-primary-color);
				}
			}
		}
		.stan-icon::v-deep {
			&.notify-info svg path {
				fill: var(--stan-positive-soft-color);
			}
			&.notify-warning svg path {
				fill: var(--stan-negative-primary-color);
			}
			&.notify-success svg path {
				fill: var(--stan-positive-soft-color);
			}
			&.notify-error svg path {
				fill: var(--stan-negative-primary-color);
			}
		}

		.notify-close {
			margin-left: 5px;
			&.notify-fact {
				background: var(--stan-gray-primary-color);
				aspect-ratio: 1;
				padding: 0;
				border-radius: 100%;
				svg {
					transform: scale(0.75);
				}
			}
		}
	}
</style>
